import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Circle, HelpCircle, Loader2, XCircle } from 'lucide-react';
import { checkAssignmentTask, getAssignment, type AssignmentState } from '../lib/api';
import type { CompleteStepResult } from '../types';

/**
 * The checklist on a graded assignment step. Each task is checked on the learner's own Mitti
 * by the server (one request per task); when the pass bar is met the server completes the step
 * and this hands the reward to the page.
 */
export default function AssignmentChecklist({
  trackId,
  stepId,
  onCompleted,
}: {
  trackId: string;
  stepId: string;
  onCompleted: (r: CompleteStepResult) => void;
}) {
  const [state, setState] = useState<AssignmentState | null>(null);
  const [busy, setBusy] = useState<number | 'all' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void getAssignment(trackId, stepId).then((res) => {
      if (res.ok) setState(res.data);
      else setError(message((res.data as { error?: string })?.error || `http_${res.status}`));
    });
  }, [trackId, stepId]);

  const checkOne = useCallback(
    async (n: number): Promise<boolean> => {
      const res = await checkAssignmentTask(trackId, stepId, n);
      if (!res.ok) {
        setError(message((res.data as { error?: string })?.error || `http_${res.status}`));
        return false;
      }
      setError(null);
      setState((s) => (s ? { ...s, results: res.data.results } : s));
      const c = res.data.completion;
      if (c?.ok && !c.alreadyDone) onCompleted(c);
      return true;
    },
    [trackId, stepId, onCompleted],
  );

  const checkAll = async () => {
    if (!state) return;
    setBusy('all');
    for (const t of state.tasks) {
      if (!(await checkOne(t.n))) break;
    }
    setBusy(null);
  };

  if (!state) {
    return (
      <div className="mt-5 rounded-xl border border-rule bg-paper-0 p-4 text-sm text-ink-500 dark:border-clay-rule dark:bg-clay-surface">
        {error ?? 'Loading your checklist…'}
      </div>
    );
  }

  const passedCount = state.tasks.filter((t) => state.results[String(t.n)]?.ok).length;
  return (
    <div className="mt-5 rounded-xl border border-rule bg-paper-0 p-4 dark:border-clay-rule dark:bg-clay-surface">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-medium text-ink-900 dark:text-paper-50">Check your work on your Mitti</p>
          <p className="mt-0.5 text-xs text-ink-500 dark:text-paper-300">
            Pass: {state.pass.min} of {state.tasks.length} tasks, including task{state.pass.must.length > 1 ? 's' : ''}{' '}
            {state.pass.must.join(' and ')}. You have {passedCount}. Check as often as you like.
          </p>
        </div>
        <button
          onClick={() => void checkAll()}
          disabled={busy !== null}
          className="shrink-0 rounded-lg bg-terracotta-500 px-3 py-2 text-sm font-semibold text-white hover:bg-terracotta-600 disabled:opacity-50"
        >
          {busy === 'all' ? 'Checking…' : 'Check everything'}
        </button>
      </div>

      {error && <p className="mt-3 text-sm text-terracotta-700 dark:text-terracotta-300">{error}</p>}

      <ol className="mt-4 space-y-2">
        {state.tasks.map((t) => {
          const r = state.results[String(t.n)];
          const running = busy === t.n;
          return (
            <li key={t.n} className="rounded-lg border border-rule p-3 dark:border-clay-rule">
              <div className="flex items-start gap-2.5">
                <span className="mt-0.5">
                  {running ? (
                    <Loader2 className="h-4 w-4 animate-spin text-ink-400" />
                  ) : r?.status === 'inconclusive' ? (
                    <HelpCircle className="h-4 w-4 text-amber-600" />
                  ) : r?.ok ? (
                    <CheckCircle2 className="h-4 w-4 text-verdant-600" />
                  ) : r ? (
                    <XCircle className="h-4 w-4 text-terracotta-600" />
                  ) : (
                    <Circle className="h-4 w-4 text-ink-300" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ink-900 dark:text-paper-50">
                    {t.n}. {t.title}
                  </p>
                  <p className="mt-0.5 text-xs text-ink-500 dark:text-paper-300">{t.hint}</p>
                  {r?.status === 'inconclusive' && (
                    <div className="mt-1.5 rounded-md bg-amber-50 px-2.5 py-2 text-xs text-ink-700 dark:bg-amber-900/20 dark:text-paper-200">
                      <p>
                        Your Mitti runs a different version of Henoyo, so we could not fully check this. It counts in your
                        favour.
                      </p>
                      {r.answer && (
                        <p className="mt-1">
                          <span className="font-semibold">What we were looking for:</span> {r.answer}
                        </p>
                      )}
                    </div>
                  )}
                  {r && r.notes.length > 0 && r.status !== 'inconclusive' && (
                    <ul className="mt-1.5 list-disc pl-4 text-xs text-ink-700 dark:text-paper-200">
                      {r.notes.map((n, i) => (
                        <li key={i}>{n}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <button
                  onClick={async () => {
                    setBusy(t.n);
                    await checkOne(t.n);
                    setBusy(null);
                  }}
                  disabled={busy !== null}
                  className="shrink-0 rounded-md border border-rule px-2.5 py-1 text-xs font-medium text-terracotta-600 hover:bg-terracotta-50 disabled:opacity-50 dark:border-clay-rule dark:text-terracotta-300 dark:hover:bg-terracotta-900/40"
                >
                  Check
                </button>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function message(code: string): string {
  switch (code) {
    case 'mitti_not_running':
      return 'Your Mitti is asleep. Go to Home, Put in the Mehnat, wait until it is running, then check again.';
    case 'no_mitti':
      return 'You do not have a Mitti yet. Ask a Khalifa to give you one.';
    case 'mitti_login_failed':
      return 'Could not sign in to your Mitti. Wait a minute after it starts, then try again.';
    case 'rate_limited':
      return 'That is a lot of checks in a short time. Give it a few minutes.';
    case 'track_not_granted':
      return 'This kushti has not been opened for you yet.';
    case 'no_token':
      return 'Your session expired. Please sign in again.';
    default:
      return 'The check did not finish. Please try again.';
  }
}
